import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { CustomFieldsCard } from "@/components/CustomFieldsCard";
import { KitchenDesigner } from "@/components/KitchenDesigner";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SendForBidDialog } from "@/components/bids/SendForBidDialog";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Send,
  Save,
  DollarSign,
  User,
  FileText,
  Download,
  Link,
  Copy,
  Settings,
  Zap,
  BookmarkPlus,
  Files,
  Factory,
  Loader2,
  Mail,
  ChevronDown,
  Clock,
  Table2,
  Gavel
} from "lucide-react";

// Categories and sub-categories
const CATEGORIES = {
  Cabinetry: ["Kitchen", "Vanity", "Built-in", "Bar", "Closet", "Other"],
  Remodel: ["Kitchen", "Bathroom", "Mudroom", "Other"],
  Refacing: [],
  Refinishing: []
};

const COUNTERTOP_OPTIONS = ["NO", "Quartz", "Marble", "Solid Wood", "Other"];
const KITCHEN_SHAPES = ["Good", "OK", "Bad"];
const PAINT_FINISHES = ["Sprayed", "Brush-Roll", "By Client"];

const DOOR_STYLES = [
  "Shaker", "Flat Panel", "Raised Panel", "Slab", "Beadboard", 
  "Glass Front", "Louvered", "Cathedral", "Custom"
];

const DOOR_COLORS = [
  "White", "Off-White", "Gray", "Navy Blue", "Black", 
  "Natural Wood", "Espresso", "Sage Green", "Cream", "Custom"
];

// Item types for line items
const ITEM_TYPES = {
  door: { label: "Door", needsHinges: true, needsHandle: true },
  drawer_front: { label: "Drawer Front", needsHandle: true, needsSlides: true, needsBox: true },
  moulding: { label: "Moulding", needsHinges: false, needsHandle: false },
  panel: { label: "Panel/End Cap", needsHinges: false, needsHandle: false },
  other: { label: "Other", needsHinges: false, needsHandle: false }
};

// Finish types with pricing per square foot
const FINISH_TYPES = {
  painted: { label: "Painted", price: 60 },
  stained: { label: "Stained", price: 70 },
  hpl: { label: "HPL (High Pressure Laminate)", price: 50 },
  thermofoil: { label: "Thermofoil", price: 35 }
};

// Item categories for promo discount logic
const ITEM_CATEGORIES = {
  product: { label: "Product/Material", appliesToPromo: true },
  service: { label: "Service", appliesToPromo: false }
};

// Hardware options for refacing estimates
const HARDWARE_OPTIONS = {
  handles: {
    label: "Handles/Pulls",
    options: [
      { id: "none", label: "No Handles", price: 0 },
      { id: "basic_bar", label: "Basic Bar Pull (5\")", price: 7 },
      { id: "modern_bar", label: "Modern Bar Pull (7\")", price: 12 },
      { id: "cup_pull", label: "Cup Pull (Bin Style)", price: 10 },
      { id: "knob_round", label: "Round Knob", price: 5 },
      { id: "knob_square", label: "Square Knob", price: 6 },
      { id: "leather_pull", label: "Leather Pull", price: 18 },
      { id: "brass_pull", label: "Brass Pull", price: 15 },
      { id: "custom", label: "Custom (price TBD)", price: 0 }
    ]
  },
  hinges: {
    label: "Soft-Close Hinges",
    options: [
      { id: "none", label: "No Hinges (Reuse Existing)", price: 0 },
      { id: "standard", label: "Standard Soft-Close", price: 5 },
      { id: "european", label: "European Premium", price: 8.50 }
    ]
  },
  drawer_slides: {
    label: "Drawer Slides",
    options: [
      { id: "none", label: "No Slides (Reuse Existing)", price: 0 },
      { id: "standard", label: "Standard Undermount Soft-Close", price: 20 },
      { id: "european", label: "European Premium Soft-Close", price: 35 }
    ]
  },
  drawer_boxes: {
    label: "Drawer Boxes",
    options: [
      { id: "none", label: "No Drawer Boxes", price: 0 },
      { id: "small", label: "Small (up to 15\")", price: 75 },
      { id: "medium", label: "Medium (15\"-24\")", price: 95 },
      { id: "large", label: "Large (24\"-36\")", price: 125 },
      { id: "custom", label: "Custom Size (price TBD)", price: 0 }
    ]
  }
};

// Parse fractional measurements like "15-5/8" into decimal
const parseFractionalMeasurement = (value) => {
  if (!value || value === "") return 0;
  
  // If it's already a number, return it
  if (typeof value === "number") return value;
  
  const str = String(value).trim();
  
  // Check for fraction format: "15-5/8" or "15 5/8"
  const mixedMatch = str.match(/^(\d+)[\s-](\d+)\/(\d+)$/);
  if (mixedMatch) {
    const whole = parseFloat(mixedMatch[1]);
    const numerator = parseFloat(mixedMatch[2]);
    const denominator = parseFloat(mixedMatch[3]);
    if (denominator !== 0) {
      return whole + (numerator / denominator);
    }
  }
  
  // Check for simple fraction: "5/8"
  const fractionMatch = str.match(/^(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const numerator = parseFloat(fractionMatch[1]);
    const denominator = parseFloat(fractionMatch[2]);
    if (denominator !== 0) {
      return numerator / denominator;
    }
  }
  
  // Try parsing as regular number
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

// Calculate square footage for an item (width * height / 144)
const calculateSqFt = (width, height) => {
  const w = parseFractionalMeasurement(width);
  const h = parseFractionalMeasurement(height);
  if (w > 0 && h > 0) {
    return (w * h) / 144; // Convert square inches to square feet
  }
  return 0;
};

// Calculate unit price based on dimensions and finish (price for 1 item)
const calculateItemPrice = (width, height, finish) => {
  const w = parseFractionalMeasurement(width);
  const h = parseFractionalMeasurement(height);
  const pricePerSqFt = FINISH_TYPES[finish]?.price || 60;
  
  if (w > 0 && h > 0) {
    // Formula: (width * height / 144) * price_per_sqft
    // 144 converts square inches to square feet
    // This returns the UNIT price (price for 1 item)
    return (w * h / 144) * pricePerSqFt;
  }
  return 0;
};

export default function EstimateBuilder() {
  const { estimateId } = useParams();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("lead_id");
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [existingEstimateInfo, setExistingEstimateInfo] = useState(null);
  
  const [estimate, setEstimate] = useState({
    category: "",
    sub_category: "",
    form_data: {
      kitchen_shape: "",
      paint_finish: "",
      countertop_type: "NO",
      door_style: "",
      door_color: "",
      kitchen_dimensions: [{ length: "", width: "", height: "" }],
      island_dimensions: { length: "", width: "" },
      countertop_sections: [{ length: "", depth: "" }],
    },
    line_items: [],
    hardware_items: [
      { type: "handles", option_id: "none", quantity: 0 },
      { type: "hinges", option_id: "none", quantity: 0 },
      { type: "drawer_slides", option_id: "none", quantity: 0 },
      { type: "drawer_boxes", option_id: "none", quantity: 0 }
    ],
    notes: "",
    terms_and_conditions: "50% deposit required to begin work. Balance due upon completion. This estimate is valid for 30 days.",
    drawing_data: null,
    designer_data: null,
    tax_rate: 13,
    discount: 0,
    deposit_percentage: 50,
    current_promo: false
  });

  // Save as Template modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Duplicate estimate modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Send to Production modal state
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [sendingToProduction, setSendingToProduction] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierCategoryFilter, setSupplierCategoryFilter] = useState("recommended");
  const [recommendedCategories, setRecommendedCategories] = useState([]);
  const [productionHistory, setProductionHistory] = useState([]);
  const [resending, setResending] = useState(null);
  const [showBidDialog, setShowBidDialog] = useState(false);

  // Designer tab state
  const [activeEstimateTab, setActiveEstimateTab] = useState("main");
  const canEditPrices = user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin";

  const isEditing = !!estimateId;

  useEffect(() => {
    fetchData();
  }, [estimateId, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const requests = [axios.get(`${API}/estimate-templates`)];
      
      if (isEditing) {
        requests.push(axios.get(`${API}/estimates/${estimateId}`));
      } else if (leadId) {
        requests.push(axios.get(`${API}/leads/${leadId}`));
      }
      
      const responses = await Promise.all(requests);
      setTemplates(responses[0].data);
      
      if (isEditing && responses[1]) {
        const existingEstimate = responses[1].data;
        setExistingEstimateInfo({
          estimate_id: existingEstimate.estimate_id,
          estimate_number: existingEstimate.estimate_number,
          status: existingEstimate.status,
          created_at: existingEstimate.created_at
        });
        setEstimate({
          category: existingEstimate.category || "",
          sub_category: existingEstimate.sub_category || "",
          form_data: existingEstimate.form_data || {},
          line_items: existingEstimate.line_items || [],
          hardware_items: existingEstimate.hardware_items || [
            { type: "handles", option_id: "none", quantity: 0 },
            { type: "hinges", option_id: "none", quantity: 0 },
            { type: "drawer_slides", option_id: "none", quantity: 0 },
            { type: "drawer_boxes", option_id: "none", quantity: 0 }
          ],
          notes: existingEstimate.notes || "",
          terms_and_conditions: existingEstimate.terms_and_conditions || "",
          drawing_data: existingEstimate.drawing_data || null,
          designer_data: existingEstimate.designer_data || null,
          tax_rate: existingEstimate.tax_rate || 13,
          discount: existingEstimate.discount || 0,
          deposit_percentage: existingEstimate.deposit_percentage || 50,
          current_promo: existingEstimate.current_promo || false
        });
        setLead(existingEstimate.lead);
      } else if (responses[1]) {
        setLead(responses[1].data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let productsSubtotal = 0;
    let servicesSubtotal = 0;
    
    estimate.line_items.forEach(item => {
      const itemTotal = (item.quantity || 1) * (item.unit_price || 0);
      if (item.item_category === "service") {
        servicesSubtotal += itemTotal;
      } else {
        productsSubtotal += itemTotal;
      }
    });
    
    // Apply 25% promo discount to products only (not services)
    const promoDiscount = estimate.current_promo ? productsSubtotal * 0.25 : 0;
    
    // Calculate hardware totals
    let hardwareTotal = 0;
    (estimate.hardware_items || []).forEach(item => {
      const hwConfig = HARDWARE_OPTIONS[item.type];
      if (hwConfig) {
        const option = hwConfig.options.find(o => o.id === item.option_id);
        if (option && item.quantity > 0) {
          const price = item.price_override != null ? item.price_override : option.price;
          hardwareTotal += price * item.quantity;
        }
      }
    });
    
    const subtotal = productsSubtotal + servicesSubtotal + hardwareTotal;
    const discountedSubtotal = subtotal - promoDiscount - estimate.discount;
    const taxAmount = discountedSubtotal * (estimate.tax_rate / 100);
    const total = discountedSubtotal + taxAmount;
    const depositRequired = total * (estimate.deposit_percentage / 100);
    
    return {
      productsSubtotal: Math.round(productsSubtotal * 100) / 100,
      servicesSubtotal: Math.round(servicesSubtotal * 100) / 100,
      hardwareTotal: Math.round(hardwareTotal * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      promoDiscount: Math.round(promoDiscount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
      depositRequired: Math.round(depositRequired * 100) / 100
    };
  };

  const addLineItem = () => {
    setEstimate({
      ...estimate,
      line_items: [
        ...estimate.line_items,
        {
          item_id: `temp_${Date.now()}`,
          description: "",
          width: "",
          height: "",
          item_type: "door",
          finish: "painted",
          item_category: "product",
          quantity: 1,
          unit_price: 0,
          notes: ""
        }
      ]
    });
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...estimate.line_items];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate unit price when dimensions or finish changes (NOT quantity)
    // Unit price = price for 1 item, Total = unit_price * quantity
    if (["width", "height", "finish"].includes(field)) {
      const item = updated[index];
      const calculatedPrice = calculateItemPrice(
        item.width,
        item.height,
        item.finish
      );
      // Only auto-update if there's a valid calculation
      if (calculatedPrice > 0) {
        updated[index].unit_price = Math.round(calculatedPrice * 100) / 100;
      }
    }
    
    setEstimate({ ...estimate, line_items: updated });
  };

  const removeLineItem = (index) => {
    setEstimate({
      ...estimate,
      line_items: estimate.line_items.filter((_, i) => i !== index)
    });
  };

  const updateHardwareItem = (type, field, value) => {
    const updated = [...(estimate.hardware_items || [])];
    const idx = updated.findIndex(h => h.type === type);
    if (idx >= 0) {
      updated[idx] = { ...updated[idx], [field]: value };
      // Reset quantity when changing to "none"
      if (field === "option_id" && value === "none") {
        updated[idx].quantity = 0;
      }
    }
    setEstimate({ ...estimate, hardware_items: updated });
  };

  // Calculate suggested hardware quantities based on line items
  const calculateSuggestedHardware = () => {
    let doorCount = 0;
    let drawerCount = 0;
    
    estimate.line_items.forEach(item => {
      const qty = parseInt(item.quantity) || 1;
      const itemType = item.item_type || "door";
      
      if (itemType === "door") {
        doorCount += qty;
      } else if (itemType === "drawer_front") {
        drawerCount += qty;
      }
    });
    
    return {
      doorCount,
      drawerCount,
      handles: doorCount + drawerCount, // 1 handle per door/drawer
      hinges: doorCount * 2, // 2 hinges per door
      drawer_slides: drawerCount, // 1 pair per drawer
      drawer_boxes: drawerCount // 1 box per drawer (optional)
    };
  };

  const applyQuickAddHardware = () => {
    const suggested = calculateSuggestedHardware();
    
    if (suggested.doorCount === 0 && suggested.drawerCount === 0) {
      toast.error("Add some doors or drawers first to use Quick Add");
      return;
    }
    
    const updated = [...(estimate.hardware_items || [])];
    
    // Update handles - use basic_bar as default
    const handleIdx = updated.findIndex(h => h.type === "handles");
    if (handleIdx >= 0 && suggested.handles > 0) {
      updated[handleIdx] = { 
        ...updated[handleIdx], 
        option_id: updated[handleIdx].option_id === "none" ? "basic_bar" : updated[handleIdx].option_id,
        quantity: suggested.handles 
      };
    }
    
    // Update hinges - use standard as default
    const hingeIdx = updated.findIndex(h => h.type === "hinges");
    if (hingeIdx >= 0 && suggested.hinges > 0) {
      updated[hingeIdx] = { 
        ...updated[hingeIdx], 
        option_id: updated[hingeIdx].option_id === "none" ? "standard" : updated[hingeIdx].option_id,
        quantity: suggested.hinges 
      };
    }
    
    // Update drawer slides - use standard as default
    const slideIdx = updated.findIndex(h => h.type === "drawer_slides");
    if (slideIdx >= 0 && suggested.drawer_slides > 0) {
      updated[slideIdx] = { 
        ...updated[slideIdx], 
        option_id: updated[slideIdx].option_id === "none" ? "standard" : updated[slideIdx].option_id,
        quantity: suggested.drawer_slides 
      };
    }
    
    // Update drawer boxes - use medium as default
    const boxIdx = updated.findIndex(h => h.type === "drawer_boxes");
    if (boxIdx >= 0 && suggested.drawer_boxes > 0) {
      updated[boxIdx] = { 
        ...updated[boxIdx], 
        option_id: updated[boxIdx].option_id === "none" ? "medium" : updated[boxIdx].option_id,
        quantity: suggested.drawer_boxes 
      };
    }
    
    setEstimate({ ...estimate, hardware_items: updated });
    toast.success(`Applied: ${suggested.handles} handles, ${suggested.hinges} hinges, ${suggested.drawer_slides} drawer slides, ${suggested.drawer_boxes} drawer boxes`);
  };

  const handleSave = async (sendAfterSave = false) => {
    if (!estimate.category) {
      toast.error("Please select a category");
      return;
    }
    
    setSaving(true);
    try {
      let response;
      
      // Prepare line items with parsed fractional measurements
      const preparedLineItems = estimate.line_items.map(item => ({
        ...item,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        width: item.width ? parseFractionalMeasurement(item.width) : null,
        height: item.height ? parseFractionalMeasurement(item.height) : null,
        width_display: item.width || null, // Keep original display value
        height_display: item.height || null,
        finish: item.finish || "painted",
        item_category: item.item_category || "product"
      }));
      
      if (isEditing) {
        response = await axios.put(`${API}/estimates/${estimateId}`, {
          ...estimate,
          line_items: preparedLineItems
        });
      } else {
        response = await axios.post(`${API}/estimates`, {
          lead_id: leadId,
          ...estimate,
          line_items: preparedLineItems
        });
      }
      
      toast.success(isEditing ? "Estimate updated!" : "Estimate created!");
      
      if (sendAfterSave && response.data.estimate_id) {
        await handleSend(response.data.estimate_id);
      } else {
        navigate(`/estimates/${response.data.estimate_id || estimateId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save estimate");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id = estimateId) => {
    try {
      const response = await axios.post(`${API}/estimates/${id}/send`);
      toast.success(response.data.message);
      navigate("/estimates");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send estimate");
    }
  };

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    if (!template) return;
    
    // Build hardware items from template or use defaults
    const defaultHardwareItems = [
      { type: "handles", option_id: "none", quantity: 0 },
      { type: "hinges", option_id: "none", quantity: 0 },
      { type: "drawer_slides", option_id: "none", quantity: 0 },
      { type: "drawer_boxes", option_id: "none", quantity: 0 }
    ];
    
    const templateHardware = template.default_hardware_items?.length > 0 
      ? template.default_hardware_items 
      : defaultHardwareItems;
    
    setEstimate({
      ...estimate,
      category: template.category,
      sub_category: template.sub_category || "",
      form_data: template.default_form_data || estimate.form_data,
      terms_and_conditions: template.default_terms || estimate.terms_and_conditions,
      tax_rate: template.default_tax_rate || estimate.tax_rate,
      deposit_percentage: template.default_deposit_percentage || estimate.deposit_percentage,
      current_promo: template.default_current_promo || false,
      hardware_items: templateHardware,
      line_items: template.default_line_items?.map(item => ({
        ...item,
        item_id: `temp_${Date.now()}_${Math.random()}`
      })) || []
    });
    
    toast.success(`Applied "${template.name}" template`);
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    if (!estimateId) {
      toast.error("Please save the estimate first before creating a template");
      return;
    }
    
    setSavingTemplate(true);
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/save-as-template`, {
        name: templateName.trim(),
        description: templateDescription.trim() || null
      });
      
      toast.success(`Template "${response.data.template_name}" created!`);
      setShowTemplateModal(false);
      setTemplateName("");
      setTemplateDescription("");
      
      // Refresh templates list
      const templatesRes = await axios.get(`${API}/estimate-templates`);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save as template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const openDuplicateModal = async () => {
    setShowDuplicateModal(true);
    setSelectedLeadId(lead?.lead_id || "");
    setLoadingLeads(true);
    
    try {
      const response = await axios.get(`${API}/leads`);
      setAvailableLeads(response.data || []);
    } catch (error) {
      setAvailableLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleDuplicateEstimate = async () => {
    if (!estimateId) {
      toast.error("Please save the estimate first");
      return;
    }
    
    setDuplicating(true);
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/duplicate`, {
        lead_id: selectedLeadId || null
      });
      
      toast.success(`Estimate duplicated as ${response.data.estimate_number}!`);
      setShowDuplicateModal(false);
      
      // Navigate to the new estimate
      navigate(`/estimates/${response.data.estimate_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate estimate");
    } finally {
      setDuplicating(false);
    }
  };

  // Determine recommended supplier categories based on estimate line items and hardware
  const getRecommendedSupplierCategories = () => {
    const categories = new Set();
    
    // Map line item types to supplier categories
    const itemTypeMapping = {
      door: "doors",
      drawer_front: "doors",
      panel: "doors",
      moulding: "moulding",
      other: "general"
    };
    
    // Check line items
    estimate.line_items.forEach(item => {
      const category = itemTypeMapping[item.item_type] || "general";
      categories.add(category);
      
      // Check finish type - if painted or stained, might need paint supplier
      if (item.finish === "painted" || item.finish === "stained") {
        categories.add("paint");
      }
    });
    
    // Check hardware items - if any hardware is selected, add hardware category
    const hasHardware = (estimate.hardware_items || []).some(
      hw => hw.option_id !== "none" && hw.quantity > 0
    );
    if (hasHardware) {
      categories.add("hardware");
    }
    
    return Array.from(categories);
  };

  // Open production modal and fetch suppliers
  const openProductionModal = async () => {
    setShowProductionModal(true);
    setSelectedSuppliers([]);
    setLoadingSuppliers(true);
    
    // Calculate recommended categories
    const recommended = getRecommendedSupplierCategories();
    setRecommendedCategories(recommended);
    setSupplierCategoryFilter("recommended");
    
    try {
      const [suppliersRes, historyRes] = await Promise.all([
        axios.get(`${API}/suppliers`),
        estimateId ? axios.get(`${API}/estimates/${estimateId}/production-history`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      const allSuppliers = suppliersRes.data || [];
      setSuppliers(allSuppliers);
      setProductionHistory(historyRes.data || []);
      
      // Auto-select suppliers that match recommended categories
      const recommendedSupplierIds = allSuppliers
        .filter(s => recommended.includes(s.category))
        .map(s => s.supplier_id);
      setSelectedSuppliers(recommendedSupplierIds);
    } catch (error) {
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Filter suppliers based on selected category filter
  const filteredSuppliers = suppliers.filter(supplier => {
    if (supplierCategoryFilter === "all") return true;
    if (supplierCategoryFilter === "recommended") {
      return recommendedCategories.includes(supplier.category);
    }
    return supplier.category === supplierCategoryFilter;
  });

  // Send production list to suppliers
  const handleSendToProduction = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error("Please select at least one supplier");
      return;
    }
    
    if (!estimateId) {
      toast.error("Please save the estimate first");
      return;
    }
    
    setSendingToProduction(true);
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/send-production-list`, {
        supplier_ids: selectedSuppliers
      });
      
      toast.success(`Production list sent to ${selectedSuppliers.length} supplier(s)!`);
      setSelectedSuppliers([]);
      // Refresh history
      const historyRes = await axios.get(`${API}/estimates/${estimateId}/production-history`).catch(() => ({ data: [] }));
      setProductionHistory(historyRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send production list");
    } finally {
      setSendingToProduction(false);
    }
  };

  // Re-send production list
  const handleResendProduction = async (productionId) => {
    setResending(productionId);
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/resend-production`, {
        production_id: productionId
      });
      toast.success(response.data.message);
      // Refresh history
      const historyRes = await axios.get(`${API}/estimates/${estimateId}/production-history`).catch(() => ({ data: [] }));
      setProductionHistory(historyRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to re-send production list");
    } finally {
      setResending(null);
    }
  };

  // Toggle supplier selection
  const toggleSupplier = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  // Open (or create) the production cut-list sheet for this estimate
  const openProductionSheet = async () => {
    if (!estimateId) return toast.error("Please save the estimate first");
    try {
      const existing = await axios.get(`${API}/production-sheets?estimate_id=${estimateId}`);
      if (existing.data?.length) return navigate(`/production-sheets/${existing.data[0].sheet_id}`);
      const created = await axios.post(`${API}/production-sheets`, { estimate_id: estimateId });
      toast.success(`${created.data.sheet_number} created from this estimate`);
      navigate(`/production-sheets/${created.data.sheet_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open production sheet");
    }
  };

  // Calculate line items totals
  const lineItemsTotals = estimate.line_items.reduce((acc, item) => {
    const sqFt = calculateSqFt(item.width, item.height) * (item.quantity || 1);
    return {
      totalSqFt: acc.totalSqFt + sqFt,
      totalQty: acc.totalQty + (parseInt(item.quantity) || 1)
    };
  }, { totalSqFt: 0, totalQty: 0 });

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimate-builder">
      {/* Header */}
      <div className="flex flex-col items-stretch justify-between gap-3 lg:flex-row lg:items-center">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEditing ? "Edit Estimate" : "New Estimate"}
            </h1>
            {lead && (
              <p className="text-slate-600">
                For: {lead.first_name} {lead.last_name}
              </p>
            )}
            {existingEstimateInfo && (
              <p className="text-sm text-slate-500">
                {existingEstimateInfo.estimate_number} • {existingEstimateInfo.status}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isEditing && existingEstimateInfo && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const link = `${window.location.origin}/estimate/${existingEstimateInfo.estimate_id}`;
                  navigator.clipboard.writeText(link);
                  toast.success("Customer link copied to clipboard!");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowTemplateModal(true)}
                className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                data-testid="save-as-template-btn"
              >
                <BookmarkPlus className="w-4 h-4 mr-2" />
                Save as Template
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={openDuplicateModal}
                className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                data-testid="duplicate-estimate-btn"
              >
                <Files className="w-4 h-4 mr-2" />
                Duplicate
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Draft"}
          </Button>
          
          {/* Send To Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="send-to-dropdown">
                <Send className="w-4 h-4 mr-2" />
                Send to
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => {
                  if (!estimateId) {
                    toast.error("Please save the estimate first");
                    return;
                  }
                  window.open(`${API}/estimates/${estimateId}/pdf`, '_blank');
                }}
                className="cursor-pointer"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleSave(true)}
                disabled={saving}
                className="cursor-pointer"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email to Customer
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => {
                  if (!estimateId) {
                    toast.error("Please save the estimate first");
                    return;
                  }
                  openProductionModal();
                }}
                className="cursor-pointer text-orange-600 focus:text-orange-600"
                data-testid="send-to-production-btn"
              >
                <Factory className="w-4 h-4 mr-2" />
                Send to Production
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => {
                  if (!estimateId) {
                    toast.error("Please save the estimate first");
                    return;
                  }
                  window.open(`${API}/estimates/${estimateId}/production-pdf`, '_blank');
                }}
                className="cursor-pointer text-orange-600 focus:text-orange-600"
                data-testid="download-production-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Production PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => estimateId ? setShowBidDialog(true) : toast.error("Please save the estimate first")} className="cursor-pointer text-emerald-700 focus:text-emerald-700" data-testid="btn-send-for-bid-trigger">
                <Gavel className="w-4 h-4 mr-2" />Send for Bid
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openProductionSheet} className="cursor-pointer text-indigo-700 focus:text-indigo-700" data-testid="open-production-sheet-btn">
                <Table2 className="w-4 h-4 mr-2" />
                Production Sheet (cut list)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile landscape hint */}
      <div className="lg:hidden flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 mb-4" data-testid="landscape-hint">
        <span className="text-base">📱</span>
        <span>For easier data entry, rotate your phone to landscape mode</span>
      </div>

      {/* Estimate Tabs: Main / Graph / Designer */}
      <div className="border-b mb-6">
        <div className="flex gap-1">
          {[
            { id: "main", label: "Main" },
            { id: "graph", label: "Graph Paper" },
            { id: "designer", label: "Designer" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveEstimateTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeEstimateTab === tab.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`estimate-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Tab */}
      {activeEstimateTab === "main" && (
      <div className="grid grid-cols-1 gap-6 pb-48">
        {/* Main Form - Full Width */}
        <div className="space-y-6">
          {/* Customer Info */}
          {lead && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Name</Label>
                    <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p className="font-medium">{lead.email || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Phone</Label>
                    <p className="font-medium">{lead.phone || "—"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Address</Label>
                    <p className="font-medium">{lead.address || "—"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Category Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Estimate Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {templates.length > 0 && (
                <div>
                  <Label>Apply Template (Optional)</Label>
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.template_id} value={template.template_id}>
                          {template.name} ({template.category})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select 
                    value={estimate.category} 
                    onValueChange={(v) => setEstimate({ ...estimate, category: v, sub_category: "" })}
                  >
                    <SelectTrigger data-testid="estimate-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(CATEGORIES).map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {estimate.category && CATEGORIES[estimate.category]?.length > 0 && (
                  <div>
                    <Label>Sub-Category</Label>
                    <Select 
                      value={estimate.sub_category} 
                      onValueChange={(v) => setEstimate({ ...estimate, sub_category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sub-category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES[estimate.category].map(sub => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Form Questions - Show for Refacing */}
          {estimate.category && (
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Kitchen Size */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Kitchen Size (ft-in)</Label>
                    <Button variant="ghost" size="sm" onClick={() => {
                      const dims = [...(estimate.form_data.kitchen_dimensions || []), { length: "", width: "", height: "" }];
                      setEstimate({ ...estimate, form_data: { ...estimate.form_data, kitchen_dimensions: dims } });
                    }}>
                      <Plus className="w-3 h-3 mr-1" /> Add Wall
                    </Button>
                  </div>
                  {(estimate.form_data.kitchen_dimensions || [{ length: "", width: "", height: "" }]).map((dim, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Length (e.g. 11'-2-1/2&quot;)"
                          value={dim.length}
                          onChange={(e) => {
                            const dims = [...(estimate.form_data.kitchen_dimensions || [])];
                            dims[idx] = { ...dims[idx], length: e.target.value };
                            setEstimate({ ...estimate, form_data: { ...estimate.form_data, kitchen_dimensions: dims } });
                          }}
                          data-testid={`kitchen-length-${idx}`}
                        />
                      </div>
                      <span className="text-slate-400 text-xs">x</span>
                      <div className="flex-1">
                        <Input
                          placeholder={"Width (e.g. 10'-6\")"}
                          value={dim.width}
                          onChange={(e) => {
                            const dims = [...(estimate.form_data.kitchen_dimensions || [])];
                            dims[idx] = { ...dims[idx], width: e.target.value };
                            setEstimate({ ...estimate, form_data: { ...estimate.form_data, kitchen_dimensions: dims } });
                          }}
                          data-testid={`kitchen-width-${idx}`}
                        />
                      </div>
                      <span className="text-slate-400 text-xs">x</span>
                      <div className="flex-1">
                        <Input
                          placeholder={"Height (e.g. 8'-0\")"}
                          value={dim.height}
                          onChange={(e) => {
                            const dims = [...(estimate.form_data.kitchen_dimensions || [])];
                            dims[idx] = { ...dims[idx], height: e.target.value };
                            setEstimate({ ...estimate, form_data: { ...estimate.form_data, kitchen_dimensions: dims } });
                          }}
                          data-testid={`kitchen-height-${idx}`}
                        />
                      </div>
                      {(estimate.form_data.kitchen_dimensions || []).length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => {
                          const dims = (estimate.form_data.kitchen_dimensions || []).filter((_, i) => i !== idx);
                          setEstimate({ ...estimate, form_data: { ...estimate.form_data, kitchen_dimensions: dims } });
                        }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {/* Island */}
                  <div className="mt-3 pt-3 border-t">
                    <Label className="text-xs font-medium text-slate-500 mb-1 block">Island</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder="Length (e.g. 6'-0&quot;)"
                          value={estimate.form_data.island_dimensions?.length || ""}
                          onChange={(e) => setEstimate({ ...estimate, form_data: { ...estimate.form_data, island_dimensions: { ...(estimate.form_data.island_dimensions || {}), length: e.target.value } } })}
                          data-testid="island-length"
                        />
                      </div>
                      <span className="text-slate-400 text-xs">x</span>
                      <div className="flex-1">
                        <Input
                          placeholder="Width (e.g. 3'-6&quot;)"
                          value={estimate.form_data.island_dimensions?.width || ""}
                          onChange={(e) => setEstimate({ ...estimate, form_data: { ...estimate.form_data, island_dimensions: { ...(estimate.form_data.island_dimensions || {}), width: e.target.value } } })}
                          data-testid="island-width"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kitchen Structure Condition</Label>
                    <Select 
                      value={estimate.form_data.kitchen_shape || ""} 
                      onValueChange={(v) => setEstimate({ 
                        ...estimate, 
                        form_data: { ...estimate.form_data, kitchen_shape: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        {KITCHEN_SHAPES.map(shape => (
                          <SelectItem key={shape} value={shape}>{shape}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>On Site Painting? (Faces and Panels)</Label>
                    <Select 
                      value={estimate.form_data.paint_finish || ""} 
                      onValueChange={(v) => setEstimate({ 
                        ...estimate, 
                        form_data: { ...estimate.form_data, paint_finish: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAINT_FINISHES.map(finish => (
                          <SelectItem key={finish} value={finish}>{finish}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Countertop Change?</Label>
                    <Select 
                      value={estimate.form_data.countertop_type || "NO"} 
                      onValueChange={(v) => {
                        const update = { ...estimate.form_data, countertop_type: v };
                        if (v === "NO") update.countertop_sections = [];
                        else if (!update.countertop_sections || update.countertop_sections.length === 0) update.countertop_sections = [{ length: "", depth: "" }];
                        setEstimate({ ...estimate, form_data: update });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTERTOP_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Door Style</Label>
                    <Select 
                      value={estimate.form_data.door_style || ""} 
                      onValueChange={(v) => setEstimate({ 
                        ...estimate, 
                        form_data: { ...estimate.form_data, door_style: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select style" />
                      </SelectTrigger>
                      <SelectContent>
                        {DOOR_STYLES.map(style => (
                          <SelectItem key={style} value={style}>{style}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Door Color</Label>
                    <Select 
                      value={estimate.form_data.door_color || ""} 
                      onValueChange={(v) => setEstimate({ 
                        ...estimate, 
                        form_data: { ...estimate.form_data, door_color: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        {DOOR_COLORS.map(color => (
                          <SelectItem key={color} value={color}>{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Countertop Sections - only if countertop type selected */}
                {estimate.form_data.countertop_type && estimate.form_data.countertop_type !== "NO" && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold">{estimate.form_data.countertop_type} Countertop Sections</Label>
                      <Button variant="ghost" size="sm" onClick={() => {
                        const sections = [...(estimate.form_data.countertop_sections || []), { length: "", depth: "" }];
                        setEstimate({ ...estimate, form_data: { ...estimate.form_data, countertop_sections: sections } });
                      }}>
                        <Plus className="w-3 h-3 mr-1" /> Add Section
                      </Button>
                    </div>
                    {(estimate.form_data.countertop_sections || []).map((sec, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
                        <div className="flex-1">
                          <Input
                            placeholder="Length (e.g. 8'-0&quot;)"
                            value={sec.length}
                            onChange={(e) => {
                              const sections = [...(estimate.form_data.countertop_sections || [])];
                              sections[idx] = { ...sections[idx], length: e.target.value };
                              setEstimate({ ...estimate, form_data: { ...estimate.form_data, countertop_sections: sections } });
                            }}
                            data-testid={`countertop-length-${idx}`}
                          />
                        </div>
                        <span className="text-slate-400 text-xs">x</span>
                        <div className="flex-1">
                          <Input
                            placeholder="Depth (e.g. 25-1/2&quot;)"
                            value={sec.depth}
                            onChange={(e) => {
                              const sections = [...(estimate.form_data.countertop_sections || [])];
                              sections[idx] = { ...sections[idx], depth: e.target.value };
                              setEstimate({ ...estimate, form_data: { ...estimate.form_data, countertop_sections: sections } });
                            }}
                            data-testid={`countertop-depth-${idx}`}
                          />
                        </div>
                        {(estimate.form_data.countertop_sections || []).length > 1 && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => {
                            const sections = (estimate.form_data.countertop_sections || []).filter((_, i) => i !== idx);
                            setEstimate({ ...estimate, form_data: { ...estimate.form_data, countertop_sections: sections } });
                          }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Line Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Door / Drawer / Mouldings Entry</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {estimate.line_items.length > 0 ? (
                <div className="space-y-3">
                  {/* Header */}
                  <div 
                    className="gap-2 text-xs font-medium text-slate-500 px-2 hidden lg:grid"
                    style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr 0.8fr 1fr 1.2fr' }}
                  >
                    <div>Description</div>
                    <div>Type</div>
                    <div>W&quot;</div>
                    <div>H&quot;</div>
                    <div>Sq Ft</div>
                    <div>Finish</div>
                    <div>Category</div>
                    <div>Qty</div>
                    <div>Price</div>
                    <div>Total</div>
                  </div>
                  
                  {/* Items */}
                  {estimate.line_items.map((item, index) => {
                    const itemSubtotal = (item.quantity || 1) * (item.unit_price || 0);
                    const itemSqFt = calculateSqFt(item.width, item.height);
                    const totalItemSqFt = itemSqFt * (item.quantity || 1);
                    return (
                      <React.Fragment key={item.item_id || index}>
                      {/* Desktop Grid */}
                      <div 
                        className="gap-2 items-center p-2 bg-slate-50 rounded-lg hidden lg:grid"
                        style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr 0.8fr 1fr 1.2fr' }}
                      >
                        <div>
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, "description", e.target.value)}
                            placeholder="Description"
                            className="text-sm h-9"
                            data-testid={`line-item-description-${index}`}
                          />
                        </div>
                        <div>
                          <Select 
                            value={item.item_type || "door"} 
                            onValueChange={(v) => updateLineItem(index, "item_type", v)}
                          >
                            <SelectTrigger className="text-xs h-9 px-2" data-testid={`line-item-type-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ITEM_TYPES).map(([key, { label }]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Input
                            value={item.width || ""}
                            onChange={(e) => updateLineItem(index, "width", e.target.value)}
                            placeholder='15-5/8'
                            className="text-xs font-mono h-9 px-2"
                            data-testid={`line-item-width-${index}`}
                          />
                        </div>
                        <div>
                          <Input
                            value={item.height || ""}
                            onChange={(e) => updateLineItem(index, "height", e.target.value)}
                            placeholder='24-3/4'
                            className="text-xs font-mono h-9 px-2"
                            data-testid={`line-item-height-${index}`}
                          />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-600 h-9 flex items-center justify-center bg-slate-100 rounded px-1">
                            {totalItemSqFt > 0 ? totalItemSqFt.toFixed(2) : "—"}
                          </div>
                        </div>
                        <div>
                          <Select 
                            value={item.finish || "painted"} 
                            onValueChange={(v) => updateLineItem(index, "finish", v)}
                          >
                            <SelectTrigger className="text-xs h-9 px-2" data-testid={`line-item-finish-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(FINISH_TYPES).map(([key, { label, price }]) => (
                                <SelectItem key={key} value={key}>
                                  {label} (${price})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Select 
                            value={item.item_category || "product"} 
                            onValueChange={(v) => updateLineItem(index, "item_category", v)}
                          >
                            <SelectTrigger className="text-xs h-9 px-2" data-testid={`line-item-category-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ITEM_CATEGORIES).map(([key, { label }]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                            min="1"
                            className="text-xs h-9 px-2"
                            data-testid={`line-item-qty-${index}`}
                          />
                        </div>
                        <div>
                          <div className="relative">
                            <DollarSign className="absolute left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, "unit_price", e.target.value)}
                              className={`text-xs pl-4 h-9 ${!canEditPrices ? "bg-slate-100" : ""}`}
                              readOnly={!canEditPrices}
                              title={!canEditPrices ? "Only managers and owners can edit prices" : ""}
                              data-testid={`line-item-price-${index}`}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700">${itemSubtotal.toFixed(2)}</span>                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-red-500"
                            onClick={() => removeLineItem(index)}
                            data-testid={`line-item-delete-${index}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {/* Mobile Card Layout */}
                      <div className="lg:hidden p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`line-item-mobile-${index}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-400">Item {index + 1}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-medium text-slate-700">${itemSubtotal.toFixed(2)}</span>                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeLineItem(index)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <Input value={item.description} onChange={(e) => updateLineItem(index, "description", e.target.value)} placeholder="Description" className="text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={item.item_type || "door"} onValueChange={(v) => updateLineItem(index, "item_type", v)}>
                            <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(ITEM_TYPES).map(([k, { label }]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={item.finish || "painted"} onValueChange={(v) => updateLineItem(index, "finish", v)}>
                            <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(FINISH_TYPES).map(([k, { label, price }]) => <SelectItem key={k} value={k}>{label} (${price})</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-400">Width"</label>
                            <Input value={item.width || ""} onChange={(e) => updateLineItem(index, "width", e.target.value)} placeholder='W' className="text-xs font-mono h-8 px-1" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400">Height"</label>
                            <Input value={item.height || ""} onChange={(e) => updateLineItem(index, "height", e.target.value)} placeholder='H' className="text-xs font-mono h-8 px-1" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400">Qty</label>
                            <Input type="number" value={item.quantity} onChange={(e) => updateLineItem(index, "quantity", e.target.value)} min="1" className="text-xs h-8 px-1" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400">Sq Ft</label>
                            <div className="text-xs font-medium text-slate-600 h-8 flex items-center justify-center bg-slate-100 rounded">{totalItemSqFt > 0 ? totalItemSqFt.toFixed(1) : "—"}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Select value={item.item_category || "product"} onValueChange={(v) => updateLineItem(index, "item_category", v)}>
                            <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(ITEM_CATEGORIES).map(([k, { label }]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
                          </Select>
                          <div className="relative">
                            <DollarSign className="absolute left-1 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                            <Input type="number" value={item.unit_price} onChange={(e) => updateLineItem(index, "unit_price", e.target.value)} className={`text-xs pl-4 h-9 ${!canEditPrices ? "bg-slate-100" : ""}`} readOnly={!canEditPrices} />
                          </div>
                        </div>
                      </div>
                      </React.Fragment>
                    );
                  })}
                  
                  {/* Totals Row */}
                  <div 
                    className="gap-2 items-center p-2 bg-indigo-50 rounded-lg border-2 border-indigo-200 mt-4 grid"
                    style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr 0.8fr 1fr 1.2fr' }}
                  >
                    <div className="text-sm font-semibold text-indigo-800">
                      TOTALS
                    </div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-indigo-700" data-testid="total-sqft">
                        {lineItemsTotals.totalSqFt.toFixed(2)}
                      </div>
                      <div className="text-xs text-indigo-500">sq ft</div>
                    </div>
                    <div></div>
                    <div></div>
                    <div className="text-center">
                      <div className="text-sm font-bold text-indigo-700" data-testid="total-qty">
                        {lineItemsTotals.totalQty}
                      </div>
                      <div className="text-xs text-indigo-500">items</div>
                    </div>
                    <div></div>
                    <div></div>
                  </div>
                  
                  {/* Pricing info footer */}
                  <div className="text-xs text-slate-500 mt-2 px-2">
                    <p>💡 Unit prices auto-calculate: (Width × Height ÷ 144) × $/sqft | Total = Price × Qty</p>
                    <p className="mt-1">
                      <span className="font-medium">Rates:</span> Painted: $60 • Stained: $70 • HPL: $50 • Thermofoil: $35/sqft
                    </p>
                    {!canEditPrices && (
                      <p className="text-amber-600 mt-1">
                        ⚠️ Price editing restricted to managers and owners
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No line items yet</p>
                  <Button variant="outline" className="mt-4" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Item
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Hardware & Accessories Section - Only show for Refacing category */}
          {estimate.category === "Refacing" && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Hardware & Accessories
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Add handles, hinges, drawer slides, and drawer boxes to complete your refacing project
                    </p>
                  </div>
                  {/* Quick Add Button */}
                  {estimate.line_items.length > 0 && (
                    <div className="text-right">
                      {(() => {
                        const suggested = calculateSuggestedHardware();
                        return (
                          <div>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={applyQuickAddHardware}
                              className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                              data-testid="quick-add-hardware-btn"
                            >
                              <Zap className="w-4 h-4 mr-1" />
                              Quick Add
                            </Button>
                            {(suggested.doorCount > 0 || suggested.drawerCount > 0) && (
                              <p className="text-xs text-slate-500 mt-1">
                                Based on {suggested.doorCount} door{suggested.doorCount !== 1 ? 's' : ''}, {suggested.drawerCount} drawer{suggested.drawerCount !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(HARDWARE_OPTIONS).map(([type, config]) => {
                    const currentItem = (estimate.hardware_items || []).find(h => h.type === type) || { option_id: "none", quantity: 0 };
                    const selectedOption = config.options.find(o => o.id === currentItem.option_id);
                    const unitPrice = currentItem.price_override != null ? currentItem.price_override : (selectedOption?.price || 0);
                    const itemTotal = selectedOption && currentItem.quantity > 0 ? unitPrice * currentItem.quantity : 0;
                    
                    return (
                      <div key={type} className="bg-slate-50 rounded-lg p-4 space-y-3">
                        <Label className="text-base font-semibold">{config.label}</Label>
                        
                        <div className="space-y-3">
                          <Select 
                            value={currentItem.option_id} 
                            onValueChange={(v) => updateHardwareItem(type, "option_id", v)}
                          >
                            <SelectTrigger className="bg-white" data-testid={`hardware-${type}-select`}>
                              <SelectValue placeholder="Select option..." />
                            </SelectTrigger>
                            <SelectContent>
                              {config.options.map(option => (
                                <SelectItem key={option.id} value={option.id}>
                                  {option.label} {option.price > 0 && `($${option.price.toFixed(2)} each)`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {currentItem.option_id !== "none" && (
                            <div className="flex items-center gap-3 flex-wrap">
                              <Label className="text-sm whitespace-nowrap">Qty:</Label>
                              <Input
                                type="number"
                                min="0"
                                value={currentItem.quantity}
                                onChange={(e) => updateHardwareItem(type, "quantity", parseInt(e.target.value) || 0)}
                                className="w-20 bg-white"
                                data-testid={`hardware-${type}-qty`}
                              />
                              <Label className="text-sm whitespace-nowrap">$/ea:</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={currentItem.price_override != null ? currentItem.price_override : (selectedOption?.price || 0)}
                                onChange={(e) => updateHardwareItem(type, "price_override", parseFloat(e.target.value) || 0)}
                                className="w-20 bg-white"
                                data-testid={`hardware-${type}-price`}
                              />
                              {itemTotal > 0 && (
                                <span className="text-sm font-medium text-indigo-600 ml-auto">
                                  ${itemTotal.toFixed(2)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Hardware total */}
                {totals.hardwareTotal > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                    <span className="font-medium text-slate-700">Hardware Subtotal:</span>
                    <span className="text-lg font-bold text-indigo-600">${totals.hardwareTotal.toFixed(2)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Custom Fields */}
          {estimateId && <CustomFieldsCard entityType="estimate" entityId={estimateId} />}

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes & Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={estimate.notes}
                  onChange={(e) => setEstimate({ ...estimate, notes: e.target.value })}
                  placeholder="Notes for your reference (not shown to customer)"
                  rows={2}
                />
              </div>
              <div>
                <Label>Terms & Conditions</Label>
                <Textarea
                  value={estimate.terms_and_conditions}
                  onChange={(e) => setEstimate({ ...estimate, terms_and_conditions: e.target.value })}
                  placeholder="Terms shown to customer"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
      )}

      {/* Graph Paper Tab */}
      {activeEstimateTab === "graph" && (
      <div className="pb-48">
        <Card>
          <CardHeader>
            <CardTitle>Kitchen Layout Sketch</CardTitle>
          </CardHeader>
          <CardContent>
            <DrawingCanvas
              value={estimate.drawing_data}
              onChange={(data) => setEstimate({ ...estimate, drawing_data: data })}
            />
          </CardContent>
        </Card>
      </div>
      )}

      {/* Designer Tab */}
      {activeEstimateTab === "designer" && (
      <div className="pb-48">
        <KitchenDesigner
          value={estimate.designer_data}
          onChange={(data) => setEstimate({ ...estimate, designer_data: data })}
        />
      </div>
      )}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Totals Section */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Products:</span>
                <span className="font-medium">${totals.productsSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Services:</span>
                <span className="font-medium">${totals.servicesSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Subtotal:</span>
                <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              {estimate.current_promo && totals.promoDiscount > 0 && (
                <div className="flex items-center gap-2 text-green-600">
                  <span className="text-sm">Promo:</span>
                  <span className="font-medium">-${totals.promoDiscount.toFixed(2)}</span>
                </div>
              )}
              {estimate.discount > 0 && (
                <div className="flex items-center gap-2 text-green-600">
                  <span className="text-sm">Discount:</span>
                  <span className="font-medium">-${estimate.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Tax ({estimate.tax_rate}%):</span>
                <span className="font-medium">${totals.taxAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Controls & Total */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {/* Promo Checkbox */}
              <label className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg cursor-pointer border border-green-200">
                <input
                  type="checkbox"
                  checked={estimate.current_promo}
                  onChange={(e) => setEstimate({ ...estimate, current_promo: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded border-green-300 focus:ring-green-500"
                  data-testid="current-promo-checkbox"
                />
                <span className="text-sm font-medium text-green-800">25% Promo</span>
              </label>

              {/* Quick Settings */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Tax:</span>
                <Input
                  type="number"
                  value={estimate.tax_rate}
                  onChange={(e) => setEstimate({ ...estimate, tax_rate: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-8 text-sm"
                  data-testid="tax-rate-input"
                />
                <span className="text-xs text-slate-500">%</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Discount:</span>
                <Input
                  type="number"
                  value={estimate.discount}
                  onChange={(e) => setEstimate({ ...estimate, discount: parseFloat(e.target.value) || 0 })}
                  className="w-20 h-8 text-sm"
                  data-testid="discount-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Deposit:</span>
                <Input
                  type="number"
                  value={estimate.deposit_percentage}
                  onChange={(e) => setEstimate({ ...estimate, deposit_percentage: parseFloat(e.target.value) || 0 })}
                  className="w-16 h-8 text-sm"
                  data-testid="deposit-percentage-input"
                />
                <span className="text-xs text-slate-500">%</span>
              </div>

              {/* Total */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right">
                  <div className="text-2xl font-bold text-indigo-600">${totals.total.toFixed(2)}</div>
                  <div className="text-xs text-amber-600 font-medium">
                    Deposit: ${totals.depositRequired.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save as Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-amber-600" />
              Save as Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Save this estimate as a reusable template. All line items, hardware selections, project details, and settings will be saved.
            </p>
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Kitchen Refacing - Premium Package"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="template-description">Description (optional)</Label>
              <Textarea
                id="template-description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of what this template includes..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700 mb-2">This template will include:</p>
              <ul className="text-slate-600 space-y-1">
                <li>• {estimate.line_items.length} line item(s)</li>
                <li>• Category: {estimate.category || "Not set"}</li>
                {estimate.category === "Refacing" && (
                  <li>• Hardware selections</li>
                )}
                <li>• Tax rate: {estimate.tax_rate}%</li>
                <li>• Deposit: {estimate.deposit_percentage}%</li>
                {estimate.current_promo && <li>• 25% Promo enabled</li>}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAsTemplate} 
              disabled={savingTemplate || !templateName.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingTemplate ? "Saving..." : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Estimate Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Files className="w-5 h-5 text-blue-600" />
              Duplicate Estimate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Create a copy of this estimate. You can keep it for the same customer or assign it to a different lead.
            </p>
            
            <div>
              <Label htmlFor="duplicate-lead">Assign to Lead</Label>
              {loadingLeads ? (
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  Loading leads...
                </div>
              ) : (
                <Select 
                  value={selectedLeadId} 
                  onValueChange={setSelectedLeadId}
                >
                  <SelectTrigger className="mt-1" data-testid="duplicate-lead-select">
                    <SelectValue placeholder="Select a lead..." />
                  </SelectTrigger>
                  <SelectContent>
                    {lead && (
                      <SelectItem value={lead.lead_id}>
                        {lead.first_name} {lead.last_name} (Current)
                      </SelectItem>
                    )}
                    {availableLeads
                      .filter(l => l.lead_id !== lead?.lead_id)
                      .map(l => (
                        <SelectItem key={l.lead_id} value={l.lead_id}>
                          {l.first_name} {l.last_name} {l.email && `- ${l.email}`}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-700 mb-2">What will be copied:</p>
              <ul className="text-blue-600 space-y-1">
                <li>• All {estimate.line_items.length} line item(s)</li>
                <li>• Category & project details</li>
                {estimate.category === "Refacing" && (
                  <li>• Hardware selections</li>
                )}
                <li>• Pricing settings (tax, deposit, promo)</li>
                <li>• Terms & conditions</li>
              </ul>
              <p className="text-blue-500 mt-2 text-xs">
                A new estimate number will be generated. Status will be set to Draft.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDuplicateEstimate} 
              disabled={duplicating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {duplicating ? "Duplicating..." : "Duplicate Estimate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Production Modal */}
      <Dialog open={showProductionModal} onOpenChange={setShowProductionModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-orange-600" />
              Send List to Production
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Send a production list (without pricing) to your suppliers. A PDF will be saved and emailed to selected suppliers.
            </p>

            {/* Email Preview */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700 mb-2">Email Preview:</p>
              <div className="text-slate-600 space-y-1">
                <p><span className="font-medium">Subject:</span> New Order - P.O &quot;{lead?.last_name || "Customer"}&quot;</p>
                <p><span className="font-medium">Body:</span></p>
                <p className="italic pl-4">Hi [Supplier Contact],<br/>
                We have a new order for you to review. Please contact us if you have any further questions.</p>
              </div>
            </div>

            {/* Production List Preview */}
            <div className="bg-orange-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-orange-700 mb-2">Production List will include:</p>
              <ul className="text-orange-600 space-y-1">
                <li>• {estimate.line_items.length} line item(s)</li>
                <li>• Total: {lineItemsTotals.totalSqFt.toFixed(2)} sq ft</li>
                <li>• Total Quantity: {lineItemsTotals.totalQty} items</li>
                <li>• Dimensions, finish, and descriptions</li>
                <li className="font-medium">• NO PRICING included</li>
              </ul>
            </div>

            {/* Supplier Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Select Suppliers to Send To
                </Label>
                <Select value={supplierCategoryFilter} onValueChange={setSupplierCategoryFilter}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="supplier-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recommended">
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-500" />
                        Recommended
                      </span>
                    </SelectItem>
                    <SelectItem value="all">All Suppliers</SelectItem>
                    <SelectItem value="doors">Doors & Panels</SelectItem>
                    <SelectItem value="hardware">Hardware & Hinges</SelectItem>
                    <SelectItem value="moulding">Moulding & Trim</SelectItem>
                    <SelectItem value="paint">Paint & Finishing</SelectItem>
                    <SelectItem value="countertops">Countertops</SelectItem>
                    <SelectItem value="general">General Supplies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Recommended categories info */}
              {recommendedCategories.length > 0 && supplierCategoryFilter === "recommended" && (
                <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Based on your estimate: {recommendedCategories.map(c => {
                    const labels = { doors: "Doors", hardware: "Hardware", moulding: "Moulding", paint: "Paint", general: "General" };
                    return labels[c] || c;
                  }).join(", ")}
                </div>
              )}
              
              {loadingSuppliers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                  <span className="ml-2 text-sm text-slate-500">Loading suppliers...</span>
                </div>
              ) : filteredSuppliers.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                  {filteredSuppliers.map(supplier => {
                    const isRecommended = recommendedCategories.includes(supplier.category);
                    return (
                      <label
                        key={supplier.supplier_id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedSuppliers.includes(supplier.supplier_id)
                            ? "bg-orange-100 border border-orange-300"
                            : "hover:bg-slate-50 border border-transparent"
                        }`}
                        data-testid={`supplier-checkbox-${supplier.supplier_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.includes(supplier.supplier_id)}
                          onChange={() => toggleSupplier(supplier.supplier_id)}
                          className="w-4 h-4 text-orange-600 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{supplier.company_name}</p>
                            {isRecommended && supplierCategoryFilter !== "recommended" && (
                              <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-amber-100 text-amber-700">
                                <Zap className="w-2 h-2 mr-0.5" />
                                Recommended
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{supplier.contact_name} • {supplier.email}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : suppliers.length > 0 ? (
                <div className="text-center py-6 border rounded-lg">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500 text-sm">No suppliers match this filter</p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setSupplierCategoryFilter("all")}
                    className="text-orange-600"
                  >
                    Show all suppliers
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6 border rounded-lg">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500 text-sm">No suppliers found</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Add suppliers in Team → Suppliers section
                  </p>
                </div>
              )}
            </div>

            {selectedSuppliers.length > 0 && (
              <div className="text-sm text-green-600 bg-green-50 rounded-lg p-2">
                ✓ {selectedSuppliers.length} supplier(s) selected - PDF will be attached to each email
              </div>
            )}

            {/* Production History */}
            {productionHistory.length > 0 && (
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  Send History ({productionHistory.length})
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                  {productionHistory.map((record) => (
                    <div
                      key={record.production_id}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                      data-testid={`history-${record.production_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 truncate">
                          {new Date(record.sent_at).toLocaleDateString()} at {new Date(record.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {record.suppliers_sent?.map(s => s.company).join(", ") || `${record.supplier_ids?.length || 0} supplier(s)`}
                          {record.resend_count > 0 && ` · Re-sent ${record.resend_count}x`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendProduction(record.production_id)}
                        disabled={resending === record.production_id}
                        className="text-orange-600 hover:text-orange-700 ml-2 shrink-0"
                        data-testid={`resend-${record.production_id}`}
                      >
                        {resending === record.production_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3 h-3 mr-1" />
                            Re-send
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowProductionModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendToProduction} 
              disabled={sendingToProduction || selectedSuppliers.length === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {sendingToProduction ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send to {selectedSuppliers.length || 0} Supplier(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendForBidDialog open={showBidDialog} onOpenChange={setShowBidDialog} estimateId={estimateId} leadId={lead?.lead_id || leadId} projectName={existingEstimateInfo?.estimate_number} scopeSummary={estimate.notes || ""} />
    </div>
  );
}


// Drawing Canvas Component - Grid paper with freehand drawing
function DrawingCanvas({ value, onChange }) {
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [penColor, setPenColor] = React.useState("#1e293b");
  const [penSize, setPenSize] = React.useState(2);
  const [tool, setTool] = React.useState("pen"); // pen, eraser

  // Initialize canvas with grid and existing drawing
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    drawGrid(ctx, rect.width, rect.height);
    if (value) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, rect.width, rect.height); };
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const drawGrid = (ctx, w, h) => {
    const gridSize = 20;
    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 0.5;
    for (let x = 0; x <= w; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let y = 0; y <= h; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }
    // Heavier lines every 5 squares
    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = 1;
    for (let x = 0; x <= w; x += gridSize * 5) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
    }
    for (let y = 0; y <= h; y += gridSize * 5) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineWidth = tool === "eraser" ? penSize * 5 : penSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : penColor;
    if (tool === "eraser") ctx.globalCompositeOperation = "destination-out";
    else ctx.globalCompositeOperation = "source-over";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    // Save drawing as data URL
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    drawGrid(ctx, rect.width, rect.height);
    onChange?.(null);
  };

  const colors = ["#1e293b", "#dc2626", "#2563eb", "#16a34a", "#9333ea", "#ea580c"];

  return (
    <div className="space-y-3" data-testid="drawing-canvas">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center border rounded-lg p-1 gap-1">
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${tool === "pen" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            onClick={() => setTool("pen")}
            data-testid="tool-pen"
          >Pen</button>
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${tool === "eraser" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            onClick={() => setTool("eraser")}
            data-testid="tool-eraser"
          >Eraser</button>
        </div>
        <div className="flex items-center gap-1">
          {colors.map(c => (
            <button
              key={c}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${penColor === c && tool === "pen" ? "border-slate-900 scale-110" : "border-transparent"}`}
              style={{ backgroundColor: c }}
              onClick={() => { setPenColor(c); setTool("pen"); }}
            />
          ))}
        </div>
        <div className="flex items-center gap-1 ml-2">
          <span className="text-xs text-slate-500">Size:</span>
          {[1, 2, 4, 6].map(s => (
            <button
              key={s}
              className={`w-7 h-7 rounded flex items-center justify-center text-xs ${penSize === s ? "bg-slate-200 font-bold" : "hover:bg-slate-100"}`}
              onClick={() => setPenSize(s)}
            >{s}</button>
          ))}
        </div>
        <button
          className="ml-auto text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1"
          onClick={clearCanvas}
          data-testid="clear-canvas"
        >Clear All</button>
      </div>
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="w-full border rounded-lg cursor-crosshair touch-none"
        style={{ height: "400px", background: "#fff" }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        data-testid="drawing-area"
      />
      <p className="text-xs text-slate-400 text-center">Draw your kitchen layout on the grid above. Works with mouse, finger, or stylus.</p>
    </div>
  );
}
